import { useRef, useState, type ReactNode } from "react";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { ImagePlus, LoaderCircle, Trash2 } from "lucide-react";
import type { MediaAsset } from "../../shared/contracts";
import { api } from "../lib/api";
import { toast } from "../lib/toast";

interface ImageUploaderProps {
  context: "post" | "chat" | "group" | "group-cover" | "avatar" | "comment" | "profile-background";
  value: MediaAsset[];
  onChange(value: MediaAsset[]): void | Promise<void>;
  max?: number;
  compact?: boolean;
  preserveMediaIds?: string[];
  renderTrigger?: (state: { disabled: boolean; open: () => void; uploading: boolean }) => ReactNode;
}

export function ImageUploader({ context, value, onChange, max = 9, compact = false, preserveMediaIds = [], renderTrigger }: ImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    if (value.length + files.length > max) {
      toast(`最多上传 ${max} 张图片`, "danger");
      return;
    }
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append("images", file));
    setUploading(true);
    try {
      const result = await api<{ items: MediaAsset[] }>(`/media?context=${context}`, { method: "POST", body: formData });
      await onChange([...value, ...result.items]);
    } catch (error) {
      toast(error instanceof Error ? error.message : "图片上传失败", "danger");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  if (renderTrigger) {
    return (
      <div className="upload-custom-trigger">
        {renderTrigger({ disabled: uploading || value.length >= max, open: () => inputRef.current?.click(), uploading })}
        <input ref={inputRef} hidden type="file" accept="image/jpeg,image/png,image/webp" multiple={max > 1} onChange={(event) => void upload(event.target.files)} />
      </div>
    );
  }

  const remove = async (item: MediaAsset) => {
    if (preserveMediaIds.includes(item.id)) {
      onChange(value.filter((media) => media.id !== item.id));
      return;
    }
    try {
      await api(`/media/${item.id}`, { method: "DELETE" });
      onChange(value.filter((media) => media.id !== item.id));
    } catch (error) {
      toast(error instanceof Error ? error.message : "无法删除图片", "danger");
    }
  };

  if (compact) {
    return (
      <div className="upload-compact">
        {value.length < max ? (
          <Button className="upload-compact-button" variant="outlined" type="button" disabled={uploading} onClick={() => inputRef.current?.click()}>
            {uploading ? <LoaderCircle className="spin" aria-hidden="true" /> : <ImagePlus aria-hidden="true" />}
            <span>{uploading ? "正在处理" : "上传图片"}</span>
          </Button>
        ) : null}
        {value.map((item) => (
          <Button className="upload-compact-remove" color="error" variant="text" size="small" type="button" key={item.id} onClick={() => void remove(item)}>
            <Trash2 aria-hidden="true" />撤销本次上传
          </Button>
        ))}
        <input ref={inputRef} className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => void upload(event.target.files)} />
      </div>
    );
  }

  return (
    <div className="upload-grid">
      {value.map((item) => (
        <div className="upload-preview" key={item.id}>
          <img src={item.url} alt="待提交图片预览" />
          <span className={`media-review media-review-${item.reviewStatus}`}>{item.reviewStatus === "pending" ? "等待图片审核" : item.reviewStatus === "approved" ? "审核通过" : "未通过"}</span>
          <Tooltip title="删除图片"><IconButton type="button" aria-label="删除这张图片" onClick={() => void remove(item)}><Trash2 aria-hidden="true" /></IconButton></Tooltip>
        </div>
      ))}
      {value.length < max ? (
        <Button className="upload-button" variant="outlined" type="button" disabled={uploading} onClick={() => inputRef.current?.click()}>
          {uploading ? <LoaderCircle className="spin" aria-hidden="true" /> : <ImagePlus aria-hidden="true" />}
          <strong>{uploading ? "正在处理" : "选择图片"}</strong><small>JPG、PNG、WebP</small>
        </Button>
      ) : null}
      <input ref={inputRef} className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" multiple={max > 1} onChange={(event) => void upload(event.target.files)} />
    </div>
  );
}
